import { useState } from 'react';
import { ArrowLeft, AlertTriangle, ChevronRight, X, Leaf, TreePine, Sparkles, Sprout } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { MUSHROOMS, WILD_MUSHROOM_WARNING } from '../lib/mushrooms';
import { useI18n } from '../i18n';
import ShareButton from '../components/ShareButton';
import LogFindModal from '../components/LogFindModal';
import Tilt from '../components/living/Tilt';
import SpeciesIcon from '../components/species/SpeciesIcon';
import SpeciesHero from '../components/species/SpeciesHero';
import MakeRecipeButton from '../components/MakeRecipeButton';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DIFFICULTY_LABEL = { let: 'easy', mellem: 'medium', svær: 'hard' };

export default function Mushrooms() {
    const { locale } = useI18n();
    const navigate = useNavigate();
    const [selected, setSelected] = useState(null);
    const [tab, setTab] = useState('cultivable');
    const [logFind, setLogFind] = useState(null);

    const filtered = MUSHROOMS.filter((m) => m.category === tab);
    const tr = (obj) => (obj && (obj[locale] || obj.en || obj.da)) || '';
    const currentMonth = new Date().getMonth() + 1;
    const inSeason = (m) => Array.isArray(m.season) && m.season.includes(currentMonth);

    return (
        <div className="px-5 pt-10 pb-32 max-w-2xl mx-auto" data-testid="mushrooms-page">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-brand-ink3 hover:text-brand-ink mb-6"><ArrowLeft size={14} /> Back</button>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">Edible mushrooms</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-brand-ink mt-1 mb-6">Mushrooms in your garden &amp; forest</h1>

            <button
                onClick={() => navigate('/mushroom-identify')}
                data-testid="btn-ai-mushroom-id"
                className="w-full mb-6 bg-brand-card hover:bg-brand-bg2 border-2 border-brand-green/30 rounded-3xl p-4 flex items-center gap-3 text-left transition-colors"
            >
                <div className="w-12 h-12 rounded-2xl bg-brand-green/10 flex items-center justify-center text-2xl">🔍</div>
                <div className="flex-1">
                    <p className="font-serif text-lg text-brand-ink">AI mushroom ID</p>
                    <p className="text-xs text-brand-ink3">Snap a photo → species, edibility, look-alikes</p>
                </div>
                <ChevronRight size={18} className="text-brand-ink3" />
            </button>

            <div className="flex gap-2 mb-6">
                <button
                    data-testid="tab-cultivable"
                    onClick={() => setTab('cultivable')}
                    className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-sm transition-colors ${tab === 'cultivable' ? 'bg-brand-green text-white' : 'bg-brand-card border border-brand-border text-brand-ink hover:bg-brand-bg2'}`}
                >
                    <Leaf size={14} strokeWidth={1.8} /> Grow your own
                </button>
                <button
                    data-testid="tab-foraged"
                    onClick={() => setTab('foraged')}
                    className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-sm transition-colors ${tab === 'foraged' ? 'bg-brand-green text-white' : 'bg-brand-card border border-brand-border text-brand-ink hover:bg-brand-bg2'}`}
                >
                    <TreePine size={14} strokeWidth={1.8} /> Wild Mushrooms
                </button>
            </div>

            {tab === 'foraged' && (
                <div className="mb-5 flex items-start gap-2 text-xs text-brand-terracotta bg-brand-terracotta/10 border border-brand-terracotta/30 rounded-2xl p-3" data-testid="wild-warning">
                    <AlertTriangle size={14} strokeWidth={1.8} className="flex-shrink-0 mt-0.5" />
                    <span>{tr(WILD_MUSHROOM_WARNING)}</span>
                </div>
            )}

            <div className="space-y-3 stagger">
                {filtered.map((m) => (
                    <Tilt
                        as="button"
                        key={m.id}
                        data-testid={`mushroom-${m.id}`}
                        onClick={() => setSelected(m)}
                        max={5}
                        className="block w-full text-left bg-brand-card border border-brand-border rounded-3xl"
                    >
                        <div className="flex items-center p-4 gap-4">
                            <SpeciesIcon kind="mushroom" id={m.id} emoji={m.emoji} name={tr(m.name)} size={60} className="depth-2" />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <div className="font-serif text-lg text-brand-ink truncate">{tr(m.name)}</div>
                                    {inSeason(m) && (
                                        <span data-testid={`badge-in-season-${m.id}`} className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-semibold bg-brand-green/15 text-brand-green px-2 py-0.5 rounded-full">
                                            <Sparkles size={9} /> In season
                                        </span>
                                    )}
                                </div>
                                <div className="text-xs text-brand-ink3 mt-0.5">
                                    {m.category === 'cultivable'
                                        ? `${DIFFICULTY_LABEL[m.difficulty] || m.difficulty} • harvest in ~${m.yield_days} days`
                                        : `Season: ${m.season.map((mo) => MONTHS[mo - 1]).join(', ')}`}
                                </div>
                            </div>
                            <ChevronRight size={16} className="text-brand-ink3" />
                        </div>
                    </Tilt>
                ))}
            </div>

            {selected && (
                <div className="fixed inset-0 z-[60] bg-brand-ink/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setSelected(null)}>
                    <div className="bg-brand-bg w-full sm:max-w-2xl rounded-t-3xl sm:rounded-3xl border border-brand-border max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <SpeciesHero kind="mushroom" id={selected.id} emoji={selected.emoji} name={tr(selected.name)} className="aspect-[16/9] rounded-t-3xl" />
                        <div className="p-6">
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-[10px] uppercase tracking-widest font-semibold text-brand-green">
                                        {selected.category === 'cultivable' ? 'Grow your own' : 'Wild mushroom'}
                                    </span>
                                    {inSeason(selected) && (
                                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-semibold bg-brand-green/15 text-brand-green px-2 py-0.5 rounded-full">
                                            <Sparkles size={9} /> In season now
                                        </span>
                                    )}
                                </div>
                                <button onClick={() => setSelected(null)} className="p-2 hover:bg-brand-bg2 rounded-full -mr-2 -mt-2"><X size={18} /></button>
                            </div>
                            <h2 className="font-serif text-3xl text-brand-ink leading-tight">{tr(selected.name)}</h2>
                            <MakeRecipeButton source={`catalog:mushroom:${selected.id}`} className="mt-3" />

                            {selected.category === 'cultivable' && (
                                <>
                                    <p className="text-sm text-brand-ink3 mt-3"><strong>Substrate:</strong> {tr(selected.substrate)}</p>
                                    <p className="text-sm text-brand-ink3"><strong>Difficulty:</strong> {DIFFICULTY_LABEL[selected.difficulty] || selected.difficulty} · harvest in ~{selected.yield_days} days</p>
                                    <p className="text-base text-brand-ink2 mt-5 leading-relaxed whitespace-pre-line">{tr(selected.instructions)}</p>
                                </>
                            )}

                            {selected.category === 'foraged' && (
                                <>
                                    <p className="text-sm text-brand-ink3 mt-3"><strong>Habitat:</strong> {tr(selected.habitat)}</p>
                                    <p className="text-sm text-brand-ink3"><strong>Season:</strong> {selected.season.map((m) => MONTHS[m - 1]).join(', ')}</p>
                                    <div className="mt-4 bg-brand-ochre/10 border border-brand-ochre/30 rounded-2xl p-3" data-testid="id-features">
                                        <div className="text-xs uppercase tracking-widest text-brand-ochre font-semibold mb-1">How to identify</div>
                                        <p className="text-sm text-brand-ink2 leading-relaxed">{tr(selected.id_features)}</p>
                                    </div>
                                    <div className="mt-3 bg-brand-terracotta/10 border border-brand-terracotta/30 rounded-2xl p-3" data-testid="lookalike-warning">
                                        <div className="text-xs uppercase tracking-widest text-brand-terracotta font-semibold mb-1 inline-flex items-center gap-1"><AlertTriangle size={11} /> Look-alike warning</div>
                                        <p className="text-sm text-brand-ink2 leading-relaxed">{tr(selected.lookalike_warning)}</p>
                                    </div>
                                </>
                            )}

                            <div className="mt-5 flex items-center justify-between border-t border-brand-border pt-4">
                                <ShareButton
                                    title={`${tr(selected.name)} · GrowDaily Mushroom Guide`}
                                    text={`🍄 ${tr(selected.name)} — discover this mushroom in GrowDaily's Mushroom Guide!`}
                                    className="text-brand-green hover:text-brand-greenDark"
                                />
                                <button
                                    data-testid="mushroom-log-find"
                                    onClick={() => setLogFind({ id: selected.id, name: tr(selected.name) })}
                                    className="inline-flex items-center gap-1.5 text-sm font-medium bg-brand-green text-white px-4 py-2 rounded-full hover:bg-brand-greenDark transition-colors"
                                >
                                    <Sprout size={15} strokeWidth={1.8} /> Log find
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <LogFindModal
                open={!!logFind}
                onClose={() => setLogFind(null)}
                defaultKind="mushroom"
                defaultSpecies={logFind}
                autoLocate
                onLogged={() => navigate('/finds-map')}
            />
        </div>
    );
}
